import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { Session, User as SupabaseUser } from "@supabase/supabase-js";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import type { UserRole } from "@/types";

function captureAuthError(error: unknown, context: string): void {
  if (typeof window !== "undefined" && "Sentry" in window) {
    const Sentry = (
      window as unknown as {
        Sentry: {
          captureException: (
            e: unknown,
            c?: { tags?: Record<string, string> },
          ) => void;
        };
      }
    ).Sentry;
    Sentry.captureException(error, {
      tags: { context: `AuthProvider.${context}` },
    });
  } else {
    console.error(`AuthProvider.${context}:`, error);
  }
}

interface UserProfile {
  id: string;
  email: string;
  display_name: string | null;
  role: UserRole;
  status: string;
  avatar_url?: string | null;
  phone_number?: string | null;
  gender?: "male" | "female" | "other" | "prefer_not_to_say" | null;
}

function buildFallbackProfileFromAuthUser(authUser: SupabaseUser): UserProfile {
  const email = authUser.email || authUser.user_metadata?.email || "";
  const displayName =
    authUser.user_metadata?.full_name ||
    authUser.user_metadata?.name ||
    (email.includes("@") ? email.split("@")[0] : null);

  return {
    id: authUser.id,
    email,
    display_name: displayName,
    role: "dancer",
    status: "pending",
    avatar_url:
      authUser.user_metadata?.avatar_url ||
      authUser.user_metadata?.picture ||
      null,
    phone_number: null,
    gender: null,
  };
}

interface AuthContextType {
  user: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  retryAuth: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  error: null,
  retryAuth: () => {},
});

let authCheckPromise: Promise<{
  session: Session;
  user: SupabaseUser;
} | null> | null = null;
let profileCheckPromise: Promise<UserProfile | null> | null = null;

async function waitForSessionWithRetry(
  maxAttempts: number = 20,
  delayMs: number = 250,
): Promise<Session | null> {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (session && !error) {
      return session;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });
  }
  return null;
}

/**
 * AuthProvider - poskytuje autentifikáciu pre celú aplikáciu
 * Spravuje auth check raz a zdieľa výsledok medzi všetkými komponentami
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const retryAuth = useCallback(() => {
    authCheckPromise = null;
    profileCheckPromise = null;
    setError(null);
    setRetryKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        // Ak Supabase nie je nakonfigurovaný, skončiť okamžite
        if (!isSupabaseConfigured) {
          if (mounted) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }

        // Ak už beží auth check, počkať na neho
        if (authCheckPromise) {
          const authResult = await authCheckPromise;
          if (!authResult) {
            if (mounted) {
              setUser(null);
              setIsLoading(false);
            }
            return;
          }

          // Pokračovať s profile checkom
          if (profileCheckPromise) {
            const profile = await profileCheckPromise;
            if (mounted) {
              setUser(profile);
              setIsLoading(false);
            }
            return;
          }
        }

        // Spustiť auth check (len raz)
        authCheckPromise = (async () => {
          // Skúsiť okamžite načítať session
          const {
            data: { session },
            error: sessionError,
          } = await supabase.auth.getSession();

          if (session && !sessionError) {
            const {
              data: { user: authUser },
            } = await supabase.auth.getUser();
            if (authUser) {
              return { session, user: authUser };
            }
          }

          // Pri OAuth callbacke môže byť session dostupná až s miernym oneskorením
          // a auth event sa môže odohrať skôr, než sa zaregistruje listener.
          const sessionAfterRetry = await waitForSessionWithRetry();
          if (sessionAfterRetry) {
            const {
              data: { user: authUser },
            } = await supabase.auth.getUser();
            if (authUser) {
              return { session: sessionAfterRetry, user: authUser };
            }
          }

          // Ak nie je session, počkať na auth state change (OAuth redirect)
          return new Promise<{ session: Session; user: SupabaseUser } | null>(
            (resolve) => {
              let resolved = false;

              const {
                data: { subscription },
              } = supabase.auth.onAuthStateChange(
                async (_event, newSession) => {
                  if (resolved || !newSession) return;

                  resolved = true;
                  const {
                    data: { user: authUser },
                  } = await supabase.auth.getUser();
                  subscription.unsubscribe();

                  if (authUser) {
                    resolve({ session: newSession, user: authUser });
                  } else {
                    resolve(null);
                  }
                },
              );

              // Pri self-hosted OAuth môže spracovanie callbacku trvať dlhšie.
              setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  subscription.unsubscribe();
                  resolve(null);
                }
              }, 8000);
            },
          );
        })();

        const authResult = await authCheckPromise;

        if (!mounted) return;

        if (!authResult || !authResult.session || !authResult.user) {
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Spustiť profile check (len raz; pri chybe resetujeme promise)
        if (!profileCheckPromise) {
          profileCheckPromise = (async (): Promise<UserProfile | null> => {
            const authUser = authResult.user;

            try {
              // Získať profil používateľa z users tabuľky
              const { data: fetchedProfile, error: profileError } =
                await supabase
                  .from("users")
                  .select("*")
                  .eq("id", authUser.id)
                  .single();

              // Ak profil neexistuje, vytvoriť ho automaticky
              let userProfile = fetchedProfile;
              if (profileError && profileError.code === "PGRST116") {
                if (import.meta.env.DEV) {
                  console.log("Profil používateľa neexistuje, vytváram...");
                }
                const email =
                  authUser.email || authUser.user_metadata?.email || "";
                const avatarUrl =
                  authUser.user_metadata?.avatar_url ||
                  authUser.user_metadata?.picture ||
                  null;

                const { data: newProfile, error: createError } = await supabase
                  .from("users")
                  .insert({
                    id: authUser.id,
                    email: email,
                    display_name:
                      authUser.user_metadata?.full_name ||
                      email.split("@")[0] ||
                      null,
                    avatar_url: avatarUrl,
                    role: "dancer",
                    status: "pending",
                  })
                  .select()
                  .single();

                if (createError || !newProfile) {
                  captureAuthError(
                    createError ?? new Error("Create profile failed"),
                    "profileCreate",
                  );
                  // OAuth session je platná, ale DB profil sa nepodarilo vytvoriť.
                  // Neodhlasujeme používateľa - použijeme fallback profil zo session.
                  return buildFallbackProfileFromAuthUser(authUser);
                }

                userProfile = newProfile;
              } else if (profileError || !userProfile) {
                captureAuthError(
                  profileError ?? new Error("Profile fetch failed"),
                  "profileFetch",
                );
                // OAuth session je platná, ale profil sa nepodarilo načítať.
                // Zachováme prihlásenie a použijeme fallback profil.
                return buildFallbackProfileFromAuthUser(authUser);
              }

              // Pridať avatar URL z OAuth metadát
              const profileWithAvatar: UserProfile = {
                ...userProfile,
                avatar_url:
                  authUser.user_metadata?.avatar_url ||
                  authUser.user_metadata?.picture ||
                  null,
              };

              return profileWithAvatar;
            } catch (e) {
              captureAuthError(e, "profileCheck");
              return buildFallbackProfileFromAuthUser(authUser);
            }
          })();
        }

        const profile = await profileCheckPromise;

        // Pri zlyhaní resetovať promise, aby retry mohol skúsiť znova
        if (!profile) {
          profileCheckPromise = null;
        }

        if (!mounted) return;

        if (!profile) {
          setError("Chyba pri načítaní profilu používateľa");
          setUser(null);
          setIsLoading(false);
          return;
        }

        setUser(profile);
        setError(null);
        setIsLoading(false);
      } catch (err) {
        authCheckPromise = null;
        profileCheckPromise = null;
        captureAuthError(err, "checkAuth");
        if (mounted) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to check authentication",
          );
          setUser(null);
          setIsLoading(false);
        }
      }
    };

    void checkAuth();

    // Listener pre auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        authCheckPromise = null;
        profileCheckPromise = null;
        setUser(null);
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Reset promises, aby sa check spustil znova
        authCheckPromise = null;
        profileCheckPromise = null;
        void checkAuth();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [retryKey]);

  return (
    <AuthContext.Provider value={{ user, isLoading, error, retryAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
